/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react';

import { jsx } from '@compiled/react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type { AiSuggestionsPlugin } from '../../../aiSuggestionsPluginType';
import { isSuggestedEditsDefaultOn } from '../../../experiments';
import { aiSuggestionsPluginKey } from '../../../pm-plugins/main';
import { useRegenerationStatus } from '../../hooks/useRegenerationStatus';
import { useSyncedSelectedSuggestion } from '../../hooks/useSyncedSelectedSuggestion';
import {
	addDiscardSuggestionAnalytics,
	fireSuggestionReasoningViewedAnalytics,
} from '../../utils/analytics';
import { getAnchorEndLocalId, getAnchorLocalId } from '../../utils/getAnchorLocalId';
import { AnchoredCard, type AnchoredCardAnchorNames } from '../AnchoredCard';
import { StatusBanner } from './StatusBanner';
import { SuggestionCard } from './SuggestionCard';

// After accepting a suggested remix, wait this long before navigating so the remix has a moment to
// render at its anchor while the card is still there.
const SUGGESTED_REMIX_NAVIGATE_DELAY_MS = 2000;

/**
 * We render this in a content component because rendering directly in a Decoration widget
 * will cause it to inherit font stylings from Editor which is not appropriate for this component.
 */
export const SuggestionCardContentComponent = ({
	api,
	onShowSidebar,
	view,
}: {
	api?: ExtractInjectionAPI<AiSuggestionsPlugin>;
	onShowSidebar?: () => void;
	view?: EditorView;
}): JSX.Element | undefined => {
	const cardRef = useRef<HTMLDivElement>(null);
	const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => () => clearTimeout(navigationTimeoutRef.current), []);

	const suggestedEditsDefaultOn = useMemo(() => isSuggestedEditsDefaultOn(), []);
	const [isHidingDeletedDiffs, setIsHidingDeletedDiffs] = useState(suggestedEditsDefaultOn);

	// Positioning-critical slice — kept synchronous (no debounce) so the CSS-anchor references
	// stay in lock-step with the ProseMirror transaction that swapped the selection.
	const { selectedSuggestion, baseLocalId } = useSyncedSelectedSuggestion(api);

	// Reset the toggle to the experiment default whenever the selected suggestion changes.
	useEffect(() => {
		setIsHidingDeletedDiffs(suggestedEditsDefaultOn);
	}, [selectedSuggestion?.id, suggestedEditsDefaultOn]);

	const anchorLocalId = selectedSuggestion ? getAnchorLocalId(selectedSuggestion) : undefined;
	const anchorEndLocalId = selectedSuggestion ? getAnchorEndLocalId(selectedSuggestion) : undefined;

	const { orderedSuggestionIds, isHidden, regeneratingSuggestionIds } =
		useSharedPluginStateWithSelector(api, ['aiSuggestions', 'editorViewMode'], (state) => {
			const { orderedSuggestionIds, isHidden, regeneratingSuggestionIds } =
				state?.aiSuggestionsState ?? {};

			return {
				orderedSuggestionIds,
				// View mode is a hard override: never show the suggestion card while read-only.
				isHidden: (isHidden ?? false) || state?.editorViewModeState?.mode === 'view',
				regeneratingSuggestionIds,
			};
		});

	// Checked against the synced `selectedSuggestion.id` (not the debounced plugin
	// state's own `selectedSuggestion`) so this always reflects the suggestion
	// actually being rendered — otherwise a quick navigation away from a
	// regenerating suggestion can transiently attribute its regenerating status to
	// whichever suggestion is selected next.
	const isRegenerating = selectedSuggestion
		? (regeneratingSuggestionIds?.has(selectedSuggestion.id) ?? false)
		: false;

	const regenerationStatus = useRegenerationStatus(selectedSuggestion, isRegenerating);
	const banner = regenerationStatus ? <StatusBanner status={regenerationStatus} /> : undefined;

	const anchorNames: AnchoredCardAnchorNames = useMemo(
		() => ({
			start: `--card-${anchorLocalId}-start`,
			end: `--card-${anchorEndLocalId}-end`,
			// Emitted only for targets whose primary anchors sit inside them, where the diff can
			// hide the anchor. Naming them unconditionally is harmless: `anchor()` treats an
			// unmatched name as invalid, exactly as it treats the primary when that is the one
			// missing, so the chain still resolves to the primary.
			outerStart: `--card-${anchorLocalId}-outer-start`,
			outerEnd: `--card-${anchorEndLocalId}-outer-end`,
			base: `--button-${baseLocalId}`,
			outerBase: `--card-${baseLocalId}-outer-base`,
		}),
		[anchorEndLocalId, anchorLocalId, baseLocalId],
	);

	const onScrollBackClick = useCallback(() => {
		if (!selectedSuggestion) {
			return;
		}
		api?.aiSuggestions?.actions.selectSuggestion(selectedSuggestion.id, {
			hideDeletedDiffs: isHidingDeletedDiffs,
		});
	}, [api, isHidingDeletedDiffs, selectedSuggestion]);

	const onToggleDeletedDiffs = useCallback(() => {
		if (!selectedSuggestion) {
			return;
		}
		const updatedHideDeletedDiffs = !isHidingDeletedDiffs;
		setIsHidingDeletedDiffs(updatedHideDeletedDiffs);
		api?.aiSuggestions?.actions.selectSuggestion(selectedSuggestion.id, {
			hideDeletedDiffs: updatedHideDeletedDiffs,
			scrollIntoView: false,
		});
	}, [api, isHidingDeletedDiffs, selectedSuggestion]);

	if (selectedSuggestion === undefined || isHidden) {
		return;
	}

	const onClose = () => {
		api?.aiSuggestions?.actions.selectSuggestion(null, { interactionPoint: 'card' });
	};

	const onDiscard = () => {
		api?.aiSuggestions?.actions.discardSuggestion(selectedSuggestion.id, 'card');

		// navigate to the next suggestion
		onNavigate('down');
	};

	const onAccept = () => {
		api?.aiSuggestions?.actions.acceptSuggestion(
			selectedSuggestion.id,
			'card',
			isHidingDeletedDiffs,
		);

		if (selectedSuggestion.actionKind === 'SUGGESTED_REMIX') {
			// Give the remix a beat to appear at the anchor before the card jumps to the next suggestion.
			navigationTimeoutRef.current = setTimeout(
				() => onNavigate('down'),
				SUGGESTED_REMIX_NAVIGATE_DELAY_MS,
			);
			return;
		}

		const shimmerDuration =
			api?.aiSuggestions?.sharedState.currentState()?.acceptShimmerDuration ?? 0;
		if (
			isExperimentEnabled('platform_editor_ai_content_suggested_edits_labs') &&
			shimmerDuration > 0
		) {
			navigationTimeoutRef.current = setTimeout(() => onNavigate('down'), shimmerDuration);
			return;
		}

		// navigate to the next suggestion
		onNavigate('down');
	};

	const onDismiss = () => {
		api?.core?.actions.execute(({ tr }) => {
			addDiscardSuggestionAnalytics(api?.analytics?.actions)(tr, selectedSuggestion, 'card');

			tr.setMeta(aiSuggestionsPluginKey, {
				action: 'REMOVE_SUGGESTION',
				id: selectedSuggestion.id,
			});
			return tr;
		});
		onNavigate('down');
	};

	const onGoodSuggestion = () => {
		api?.aiSuggestions?.actions.submitSuggestionFeedback(selectedSuggestion.id, 'good', {
			interactionPoint: 'card',
		});
	};

	const onBadSuggestion = () => {
		api?.aiSuggestions?.actions.submitSuggestionFeedback(selectedSuggestion.id, 'bad', {
			interactionPoint: 'card',
		});
	};

	const onReasoningViewed = () => {
		fireSuggestionReasoningViewedAnalytics(api?.analytics?.actions, selectedSuggestion, 'card');
	};

	const onNavigate = (direction: 'up' | 'down') => {
		const currentId = selectedSuggestion.id;
		if (!orderedSuggestionIds || orderedSuggestionIds?.length === 0) {
			return undefined;
		}
		const idx = orderedSuggestionIds.indexOf(currentId);
		if (idx === -1) {
			return undefined;
		}
		const delta = direction === 'up' ? -1 : 1;
		const nextIdx = (idx + delta + orderedSuggestionIds.length) % orderedSuggestionIds.length;
		const nextId = orderedSuggestionIds[nextIdx];
		// When only one suggestion exists, the modulo wraps back to the current id.
		// Skip navigation in that case so we don't re-select the suggestion that's about to be removed.
		if (nextId === currentId) {
			return undefined;
		}
		api?.aiSuggestions?.actions.selectSuggestion(nextId, { interactionPoint: 'card' });
	};

	return (
		<AnchoredCard
			// Remounts when a different suggestion is opened, which replays the fade-in — the
			// elements are otherwise reused across navigations.
			key={selectedSuggestion.id}
			anchorNames={anchorNames}
			cardRef={cardRef}
			onScrollBack={onScrollBackClick}
			targetId={baseLocalId}
			targetLocalId={anchorLocalId}
			view={view}
		>
			<SuggestionCard
				ref={cardRef}
				suggestion={selectedSuggestion}
				onNavigate={
					orderedSuggestionIds && orderedSuggestionIds.length > 1 ? onNavigate : undefined
				}
				onClose={onClose}
				onDiscard={onDiscard}
				onDismiss={onDismiss}
				onAccept={onAccept}
				onGoodSuggestion={onGoodSuggestion}
				onBadSuggestion={onBadSuggestion}
				onReasoningViewed={onReasoningViewed}
				onShowSidebar={onShowSidebar}
				onToggleDeletedDiffs={onToggleDeletedDiffs}
				isHidingDeletedDiffs={isHidingDeletedDiffs}
				isRegenerating={isRegenerating}
				showComparisonButton={suggestedEditsDefaultOn}
				banner={banner}
			/>
		</AnchoredCard>
	);
};
